import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Clock, AlertTriangle, Zap } from 'lucide-react';

const TrialBanner = ({ trialActive, trialDaysRemaining, trialEndDate, isFixed = false }) => {
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    if (!trialActive || !trialEndDate) return;

    const updateCountdown = () => {
      const endDate = new Date(trialEndDate);
      const now = new Date();
      const diff = endDate - now;

      if (diff <= 0) {
        setTimeRemaining('Trial ended');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (days > 0) {
        setTimeRemaining(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s`);
      } else {
        setTimeRemaining(`${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000); // Update every second for urgency

    return () => clearInterval(interval);
  }, [trialActive, trialEndDate]);

  // Trial has ended - show expired banner (urgent blue)
  if (!trialActive && trialDaysRemaining === 0) {
    const baseClasses = isFixed 
      ? "fixed top-0 left-0 right-0 z-[60] lg:left-64" 
      : "";
    
    return (
      <div className={`${baseClasses} bg-gradient-to-r from-[#0A0F1E] to-[#1B5AFF]`} data-testid="trial-banner-expired">
        <div className="px-4 py-2.5">
          <div className="flex items-center justify-between gap-3 max-w-screen-xl mx-auto">
            <div className="flex items-center gap-3 min-w-0">
              <AlertTriangle className="w-5 h-5 text-white flex-shrink-0 animate-pulse" />
              <div className="min-w-0">
                <p className="text-white font-semibold text-sm truncate">
                  Trial ended. Premium features locked
                </p>
                <p className="text-white/80 text-xs hidden sm:block">
                  Upgrade now to continue searching talent and sending reach-outs
                </p>
              </div>
            </div>
            <Link to="/employer/settings" className="flex-shrink-0">
              <Button 
                size="sm"
                className="bg-white text-[#1B5AFF] hover:bg-white/90 font-semibold rounded-lg shadow-lg text-xs sm:text-sm px-3 sm:px-4" 
                data-testid="trial-upgrade-btn"
              >
                <Zap className="w-3.5 h-3.5 mr-1" />
                Upgrade
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Active trial - show countdown banner
  if (trialActive) {
    const isUrgent = trialDaysRemaining <= 1;
    const baseClasses = isFixed 
      ? "fixed top-0 left-0 right-0 z-[60] lg:left-64" 
      : "";
    
    const bgColor = isUrgent 
      ? "bg-gradient-to-r from-[#F0B429] to-[#F0B429]/80" 
      : "bg-gradient-to-r from-[#1B5AFF] to-[#00C9A7]";
    
    return (
      <div className={`${baseClasses} ${bgColor}`} data-testid="trial-banner-active">
        <div className="px-4 py-2.5">
          <div className="flex items-center justify-between gap-3 max-w-screen-xl mx-auto">
            <div className="flex items-center gap-3 min-w-0">
              <Clock className={`w-5 h-5 text-white flex-shrink-0 ${isUrgent ? 'animate-pulse' : ''}`} />
              <div className="min-w-0">
                <p className="text-white font-semibold text-sm">
                  <span className="hidden sm:inline">Free Trial: </span>
                  <span className="font-mono bg-white/20 px-2 py-0.5 rounded text-xs sm:text-sm">
                    {timeRemaining}
                  </span>
                  <span className="hidden sm:inline ml-1">remaining</span>
                </p>
                {isUrgent && (
                  <p className="text-white/90 text-xs hidden sm:block">
                    Your trial expires soon! Don't lose access.
                  </p>
                )}
              </div>
            </div>
            <Link to="/employer/settings" className="flex-shrink-0">
              <Button 
                size="sm"
                className={`${isUrgent ? 'bg-white text-[#F0B429] hover:bg-white/90' : 'bg-white text-[#1B5AFF] hover:bg-white/90'} font-semibold rounded-lg shadow-lg text-xs sm:text-sm px-3 sm:px-4`}
                data-testid="trial-upgrade-btn"
              >
                <Zap className="w-3.5 h-3.5 mr-1" />
                Upgrade
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default TrialBanner;
